import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLicenses, createLicense, activateLicense, deactivateLicense } from '../../services/api';
import { pill } from '../pill';
import Button from '../ui/Button';
import CopyButton from '../ui/CopyButton';
import LicenseCodeInfo from './LicenseCodeInfo';

const empty = {
  clientName: '', code: '', clientEmail: '', maxUsers: '', uniqueUserWindowDays: '30',
  validFrom: new Date().toISOString().slice(0, 10),
  validUntil: ''
};

export default function LicensesPage() {
  const [licenses, setLicenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = () => getLicenses().then(setLicenses);
  useEffect(() => { load(); }, []);

  const handleCreate = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createLicense({
        ...form,
        maxUsers: parseInt(form.maxUsers),
        uniqueUserWindowDays: parseInt(form.uniqueUserWindowDays) || 30,
        validFrom: form.validFrom + 'T00:00:00',
        validUntil: form.validUntil + 'T23:59:59',
      });
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      // ex. code déjà utilisé (409) ou invalide (400)
      setError(err.response?.data?.error || 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id, active) => {
    if (active) await deactivateLicense(id);
    else await activateLicense(id);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Licences</h1>
        <Button variant={showForm ? 'secondary' : 'primary'} onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Annuler' : '+ Nouvelle licence'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{ ...card, marginBottom: '1.5rem', display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <div style={{ gridColumn: '1 / -1', fontWeight: 600, color: '#6b7280' }}>Nouvelle licence</div>
          <Field label="Nom du client" value={form.clientName} onChange={v => setForm({ ...form, clientName: v })} required />
          <Field label="Code (ex. SNCF, CA31 — vide = dérivé du nom)" value={form.code}
                 onChange={v => setForm({ ...form, code: v.toUpperCase() })} />
          <Field label="Email" type="email" value={form.clientEmail} onChange={v => setForm({ ...form, clientEmail: v })} required />
          <Field label="Quota utilisateurs" type="number" value={form.maxUsers} onChange={v => setForm({ ...form, maxUsers: v })} required />
          <Field label="Fenêtre utilisateurs uniques (jours)" type="number" value={form.uniqueUserWindowDays} onChange={v => setForm({ ...form, uniqueUserWindowDays: v })} required />
          <Field label="Valide à partir du" type="date" value={form.validFrom} onChange={v => setForm({ ...form, validFrom: v })} required />
          <Field label="Valide jusqu'au" type="date" value={form.validUntil} onChange={v => setForm({ ...form, validUntil: v })} required />
          {error && <div style={{ gridColumn: '1 / -1', color: '#c62828', fontSize: '0.85rem' }}>{error}</div>}
          <div style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Créer la licence'}
            </Button>
          </div>
        </form>
      )}

      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1100px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              {['Client', <>Code<LicenseCodeInfo /></>, 'Email', 'Clé', 'Quota', 'Users actifs', 'Fenêtre', 'Expire le', 'Statut', ''].map((h, i) =>
                <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {licenses.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #ececec' }}
                  className="ui-row">
                <td style={td}>{l.clientName}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <code style={{ color: '#232853', fontWeight: 700, marginRight: '0.2rem' }}>{l.code}</code>
                  <CopyButton value={l.code} />
                </td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.85rem' }}>{l.clientEmail}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#8a8f99' }}>{l.licenseKey}</td>
                <td style={td}>{l.maxUsers}</td>
                <td style={{ ...td, fontWeight: 700, color: l.currentUniqueUsers > l.maxUsers ? '#c62828' : '#444444' }}>
                  {l.currentUniqueUsers}
                </td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.85rem' }}>{l.uniqueUserWindowDays}j</td>
                <td style={{ ...td, fontSize: '0.85rem', color: '#6b7280' }}>
                  {new Date(l.validUntil).toLocaleDateString('fr-FR')}
                </td>
                <td style={td}>
                  <span style={pill(l.active ? '#e2f3e8' : '#ffffff', l.active ? '#1a7f37' : '#8a8f99')}>
                    {l.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ ...td, display: 'flex', gap: '0.5rem' }}>
                  <Button size="sm" onClick={() => navigate(`/licenses/${l.id}`)}>Détail</Button>
                  <Button size="sm" variant={l.active ? 'warning' : 'success'} onClick={() => toggle(l.id, l.active)}>
                    {l.active ? 'Désactiver' : 'Activer'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        style={{ width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', borderRadius: '6px', boxSizing: 'border-box' }}
      />
    </div>
  );
}

const card = { background: '#ffffff', borderRadius: '12px', padding: '1.5rem' };
const td = { padding: '0.85rem 1rem', color: '#444444' };
